// Header: SHOP slide-down panel and in-place search. Markup: sections/header.liquid.
// The MENU drawer is a <dialog class="drawer"> opened by theme.js. State lives in nav-open/search-open attributes.
const hoverable = window.matchMedia('(hover: hover) and (pointer: fine)');

class SukiHeader extends HTMLElement {
  connectedCallback() {
    this.btns = [...this.querySelectorAll('[data-mega-btn]')];
    this.searchBtn = this.querySelector('[data-search-open]');
    this.input = this.querySelector('.hsearch__input');
    this.scope = this.querySelector('predictive-search');

    this.btns.forEach((btn) => {
      const panel = this.panelFor(btn);
      btn.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse' || !hoverable.matches) return;
        // ~100ms intent delay so a pass-over doesn't flash the panel.
        clearTimeout(this.openTimer);
        this.openTimer = setTimeout(() => this.open(btn), 100);
      });
      btn.addEventListener('pointerleave', () => clearTimeout(this.openTimer));
      [btn, panel].forEach((el) => {
        el.addEventListener('pointerenter', () => clearTimeout(this.closeTimer));
        el.addEventListener('pointerleave', (e) => {
          if (e.pointerType !== 'mouse') return;
          this.closeTimer = setTimeout(() => this.open(null), 200);
        });
      });
    });

    this.addEventListener('click', (e) => {
      const t = e.target;
      const btn = t.closest('[data-mega-btn]');
      // A mouse click on an already hover-opened panel keeps it open; keyboard/touch toggles.
      if (btn) return this.open(btn === this.current && !(e.detail && hoverable.matches) ? null : btn);
      if (t.closest('.mega-scrim')) return this.open(null);
      if (t.closest('[data-search-open]')) {
        e.preventDefault();
        return this.search(true);
      }
      if (t.closest('[data-search-close]')) this.search(false);
    });

    this.addEventListener('focusout', (e) => {
      if (this.current && e.relatedTarget && !this.current.contains(e.relatedTarget) && !this.panelFor(this.current).contains(e.relatedTarget)) this.open(null);
    });

    // The menu drawer sits outside <suki-header>: SEARCH there closes it and opens the inline search.
    document.getElementById('MenuDrawer')?.addEventListener('click', (e) => {
      if (!e.target.closest('[data-search-open]')) return;
      e.preventDefault();
      e.currentTarget.close();
      this.search(true);
    });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      if (this.panelFor(this.current)?.contains(document.activeElement)) this.current.focus();
      this.open(null);
      this.search(false);
    });

    document.addEventListener('click', (e) => {
      // The drawer's SEARCH link sits outside the header; its own click must not close the search it just opened.
      if (this.contains(e.target) || e.target.closest('[data-search-open]')) return;
      this.open(null);
      this.search(false);
    });
  }

  panelFor(btn) {
    return btn && document.getElementById(btn.getAttribute('aria-controls'));
  }

  // open(btn) shows that panel and closes any other; open(null) closes all.
  open(btn) {
    clearTimeout(this.closeTimer);
    if (btn === this.current) return;
    if (btn) this.search(false);
    // Only open-from-rest and close animate.
    const switching = !!(btn && this.current);
    this.toggleAttribute('nav-switch', switching);
    this.btns.forEach((b) => {
      b.setAttribute('aria-expanded', b === btn);
      this.panelFor(b).classList.toggle('is-open', b === btn);
    });
    this.current = btn;
    this.toggleAttribute('nav-open', !!btn);
    if (!switching) return;
    this.offsetHeight; // flush styles so the swap commits with transitions off
    this.removeAttribute('nav-switch');
  }

  search(open) {
    if (open === this.hasAttribute('search-open')) return;
    if (open) this.open(null);
    this.toggleAttribute('search-open', open);
    this.searchBtn?.setAttribute('aria-expanded', open);
    // preventScroll: the header is sticky, and html scroll-padding-top would otherwise scroll the page to the top.
    if (open) return this.input.focus({ preventScroll: true });
    const hadFocus = this.contains(document.activeElement);
    this.input.value = '';
    this.scope.clear();
    if (hadFocus) this.searchBtn?.focus({ preventScroll: true });
  }
}

customElements.define('suki-header', SukiHeader);
